import { Dispatch, forwardRef, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'
import { MathfieldElement, VirtualKeyboardLayout, VirtualKeyboardName } from 'mathlive'

import {
  ensureDefaultVirtualKeyboardLayout,
  InAppKeyboardLayout,
} from '~/in-app-keyboard/constants/inAppKeyboard.const'
import { normalizeLatexAnswer } from '~/in-app-keyboard/utils/normalizeLatexAnswer'
import { useAppDispatch, useAppSelector } from '~/redux/hooks'
import { selectBossIsBusy, selectHasStartedQuiz } from '~/bootcamp/slices/bootcamp-quiz.slice'
import {
  selectIsRewardFeedbackScreenOpen,
  selectShouldResetInput,
  setShouldResetInputActionCreator,
} from '~/bootcamp/slices/bootcamp-activity.slice'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import KeyboardIcon from '~/components/Icons/svgs/KeyboardIcon'
import { Breakpoint, useCheckBreakpoint } from '~/utils/useBreakpoint'

interface InputKeyboardProps {
  autoFocus?: boolean
  className?: string
  mathFieldClassName?: string
  disabled?: boolean
  focused: boolean
  maxDigits?: number
  onChange: (value: string | number) => void
  isLatexNewLineEnterKey?: boolean
  onEnterPress?: () => void
  isBossBattle?: boolean
  setFocused: Dispatch<SetStateAction<boolean>>
  showKeyboardButton?: boolean
  style?: React.CSSProperties
  type: 'number' | 'text'
  layouts?: VirtualKeyboardLayout[] | VirtualKeyboardName[]
  value?: string | number
  normalizedInput?: boolean
}

function remeasureVirtualKeyboard(): void {
  setTimeout(() => {
    const keyboard = window.mathVirtualKeyboard as unknown as { visible?: boolean; adjustBoundingRect?: () => void }
    if (keyboard?.visible) keyboard.adjustBoundingRect?.()
  }, 120)
}

function getDecimalSeparator(locale: string): string {
  return (1.1).toLocaleString(locale).replace(/\d/g, '').charAt(0)
}

const InputKeyboard = ({
  autoFocus,
  className,
  mathFieldClassName,
  disabled,
  focused,
  maxDigits,
  onChange,
  isLatexNewLineEnterKey = false,
  onEnterPress,
  isBossBattle = false,
  setFocused,
  showKeyboardButton,
  style,
  type,
  layouts,
  value,
  normalizedInput = true,
}: InputKeyboardProps): React.ReactElement => {
  const mathFieldRef = useRef<MathfieldElement>(null)
  const [currentValue, setCurrentValue] = useState<string>('')
  const [isFontSet, setIsFontSet] = useState(false)
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false)
  const [touchStart, setTouchStart] = useState(0)

  const hasStartedQuiz = useAppSelector(selectHasStartedQuiz)
  const showBossAnimation = useAppSelector(selectBossIsBusy)
  const showBoss = (!hasStartedQuiz ? true : showBossAnimation) ?? false
  const isDesktop = useCheckBreakpoint({ gt: Breakpoint.MD })

  const userLocale = typeof navigator !== 'undefined' ? navigator.language : 'en-US'
  const decimalSeparator = getDecimalSeparator(userLocale)
  const shouldResetInput = useAppSelector(selectShouldResetInput)
  const isRewardScreenOpen = useAppSelector(selectIsRewardFeedbackScreenOpen)
  const dispatch = useAppDispatch()

  const onFocus = useCallback(
    (isButton?: boolean) => {
      if (disabled || (isDesktop && showKeyboardButton && !isButton)) return
      mathFieldRef.current?.focus()
      setFocused(true)
      setIsKeyboardVisible(true)
    },
    [setFocused, disabled, isDesktop, showKeyboardButton],
  )

  const onBlur = (): void => {
    setTimeout(() => {
      const active = document.activeElement
      const mathField = mathFieldRef.current
      if (mathField && (active === mathField || mathField.contains(active))) return
      setFocused(false)
    }, 50)
  }

  const handleMathFieldInput = (evt: React.ChangeEvent<MathfieldElement>): void => {
    const mathfield = evt.target
    const latex = mathfield.getValue('latex')

    // maxDigits limits what the user sees, so measure the ASCII rendition: one keycap can
    // insert many LaTeX characters (\text{b} is 8 of them for a single letter), and slicing
    // raw LaTeX mid-command corrupts the field (e.g. '\text{b}' becomes '\te'). Reject the
    // whole edit instead of truncating.
    if (maxDigits && normalizeLatexAnswer(latex).length > maxDigits) {
      mathfield.setValue(currentValue)
      return
    }

    setCurrentValue(latex)
    const normalized = normalizedInput && decimalSeparator === ',' ? latex.replaceAll(',', '.') : latex
    onChange(normalized)
  }

  const onEnterPressCallback = useCallback(
    (evt: InputEvent) => {
      if (evt.inputType === 'insertLineBreak') {
        evt.preventDefault()
        evt.stopPropagation() // Add this to prevent bubbling
        evt.stopImmediatePropagation() // Also add this for extra safety

        onEnterPress?.()
      }
    },
    [onEnterPress],
  )

  const onResetInput = useCallback(() => {
    if (mathFieldRef?.current) {
      mathFieldRef.current.setValue('')
      setCurrentValue('')
      onChange('')
    }
  }, [onChange])

  useEffect(() => {
    if (shouldResetInput) {
      onResetInput()
      dispatch(setShouldResetInputActionCreator(false))
    }
  }, [dispatch, onResetInput, shouldResetInput])

  useEffect(() => {
    ensureDefaultVirtualKeyboardLayout()
  }, [])

  // The math-field owns its own content, so a parent that clears `value` (new activity, or the
  // clear button) has to have the empty value pushed in or the old digits stay on screen.
  useEffect(() => {
    if (value === '' && mathFieldRef.current) {
      mathFieldRef.current.setValue('')
      setCurrentValue('')
    }
  }, [value])

  // Show/hide virtual keyboard on focus state
  useEffect(() => {
    if (isRewardScreenOpen) {
      window.mathVirtualKeyboard?.hide()
      return
    }
    const timeout = setTimeout(() => {
      if (focused) {
        if (layouts) window.mathVirtualKeyboard.layouts = layouts
        window.mathVirtualKeyboard?.show()
        remeasureVirtualKeyboard()
      } else {
        if (isDesktop && mathFieldRef.current?.closest('[data-fullscreen-activity]')) return
        // Only hide if no other math-field is focused
        const activeMathFields = document.querySelectorAll('math-field:focus')
        if (activeMathFields.length === 0) {
          setTimeout(() => {
            window.mathVirtualKeyboard?.hide()
          }, 50)
        }
      }
    }, 100)
    return () => clearTimeout(timeout)
  }, [focused, isRewardScreenOpen, layouts, isDesktop])

  // Setup mathlive config once
  useEffect(() => {
    const mathField = mathFieldRef.current
    if (typeof window === 'undefined' || !mathField) return

    window.mathVirtualKeyboard.editToolbar = 'none'
    window.mathVirtualKeyboard.layouts = layouts || InAppKeyboardLayout.HoMCompact_keyboard()

    MathfieldElement.fontsDirectory = '../../public/fonts'
    MathfieldElement.soundsDirectory = '/sounds'

    mathField.menuItems = []
    mathField.style.border = 'white'
    mathField.mathModeSpace = `\\enspace`
    mathField.inlineShortcuts = {}

    const handleEscape = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') ev.preventDefault()
    }

    const handlePaste = (ev: ClipboardEvent): void => {
      const text = ev.clipboardData?.getData('text/plain')
      if (!text) return
      ev.preventDefault()
      ev.stopPropagation()
      ev.stopImmediatePropagation()

      const normalized = normalizeMixedPaste(text)
      mathField.executeCommand(['insert', normalized])
    }

    function normalizeMixedPaste(input: string): string {
      const parts = input.split(/(\\\([^]*?\\\)|\$[^]*?\$)/g)
      return parts
        .map((part) => {
          // Math segments → leave untouched
          if (part.startsWith('\\(') || (part.startsWith('$') && part.endsWith('$'))) {
            return part
          }
          // Text segments → preserve spaces
          return normalizePlainText(part)
        })
        .join('')
    }

    function normalizePlainText(text: string): string {
      return text.replace(/\s+/g, ' ').replace(/ /g, '\\enspace ')
    }

    if (value !== undefined) {
      mathField.setValue(String(value))
      setCurrentValue(String(value))
    }

    mathField.addEventListener('paste', handlePaste, { capture: true })
    mathField.addEventListener('keydown', handleEscape, { capture: true })

    if (isLatexNewLineEnterKey) {
      mathField.keybindings = [
        ...mathField.keybindings,
        {
          key: 'enter',
          command: ['insert', '\\\\'],
        },
      ]
    }

    return () => {
      window.mathVirtualKeyboard?.hide()
      mathField.removeEventListener('keydown', handleEscape, { capture: true })
      mathField.removeEventListener('paste', handlePaste, { capture: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!layouts) return
    window.mathVirtualKeyboard.layouts = layouts
    remeasureVirtualKeyboard()
  }, [layouts])

  // Autofocus for boss mode
  useEffect(() => {
    if ((autoFocus || isBossBattle) && !showBoss) {
      const timeout = setTimeout(onFocus, 500)
      return () => clearTimeout(timeout)
    }
  }, [autoFocus, isBossBattle, showBoss, onFocus])

  // Manual autofocus fallback
  useEffect(() => {
    if (autoFocus && !isBossBattle) {
      mathFieldRef.current?.focus()
    }
  }, [autoFocus, isBossBattle])

  // Handle Enter key. MathLive maps the physical NumpadEnter to a literal "\r" character
  // instead of the return command, so it never reaches the insertLineBreak path: catch it
  // on keydown and route it to the same behaviour as the main Enter.
  useEffect(() => {
    const inputField = mathFieldRef.current
    if (!inputField) return
    const onNumpadEnter = (ev: KeyboardEvent): void => {
      if (ev.code !== 'NumpadEnter' || ev.isComposing) return
      ev.preventDefault()
      ev.stopPropagation()
      if (isLatexNewLineEnterKey) inputField.executeCommand(['insert', '\\\\'])
      else onEnterPress?.()
    }
    inputField.addEventListener('keydown', onNumpadEnter, { capture: true })
    if (!isLatexNewLineEnterKey) inputField.addEventListener('beforeinput', onEnterPressCallback)
    return () => {
      inputField.removeEventListener('keydown', onNumpadEnter, { capture: true })
      inputField.removeEventListener('beforeinput', onEnterPressCallback)
    }
  }, [isLatexNewLineEnterKey, onEnterPressCallback, onEnterPress])

  // Enforce max digits on manual input
  useEffect(() => {
    if (!maxDigits) return
    const inputField = mathFieldRef.current
    if (!inputField) return

    const handleBeforeInput = (e: InputEvent): void => {
      const allowedTypes = ['deleteContentBackward', 'deleteContentForward', 'moveCursor']
      if (allowedTypes.includes(e.inputType)) return

      const visibleValue = normalizeLatexAnswer(inputField.getValue('latex'))
      if (visibleValue.length >= maxDigits) {
        e.preventDefault()
      }
    }

    inputField.addEventListener('beforeinput', handleBeforeInput)
    return () => inputField.removeEventListener('beforeinput', handleBeforeInput)
  }, [maxDigits])

  // Inject global font styles inside shadow DOM
  useEffect(() => {
    if (isFontSet || !mathFieldRef.current) return

    const shadowRoot = mathFieldRef.current.shadowRoot
    if (shadowRoot) {
      const styleEl = document.createElement('style')
      styleEl.textContent = `
        .ML__cmr, .ML__mathit {
          font-family: 'Poppins', sans-serif !important;
        },
      `
      shadowRoot.appendChild(styleEl)
      setIsFontSet(true)
    }
  }, [isFontSet])

  const onKeyboardIconClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation()
    if (disabled) return
    if (isKeyboardVisible) {
      setIsKeyboardVisible(false)
    } else {
      setTimeout(() => onFocus(true), 100)
    }
  }

  return (
    <div className={`relative ${className}`} style={style}>
      {showKeyboardButton && (
        <button
          className="absolute top-2 right-2 py-1 px-2 cursor-pointer z-10 rounded-small hover:bg-gray-platinum duration-300"
          onClick={onKeyboardIconClick}
        >
          <SVGIcon icon={KeyboardIcon} pathClassName="text-blue-cold" />
        </button>
      )}
      <math-field
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        className={mathFieldClassName}
        mathVirtualKeyboardPolicy="sandboxed"
        onBlur={onBlur}
        onFocus={() => {
          if (isDesktop) {
            setTimeout(onFocus, 100)
          }
        }}
        onInput={handleMathFieldInput}
        onTouchEnd={() => {
          if (Date.now() - touchStart < 200) {
            setTimeout(onFocus, 100)
          }
        }}
        onTouchStart={() => {
          setTouchStart(Date.now())
        }}
        ref={mathFieldRef}
        spellCheck="false"
        step="any"
        style={{ width: '100%', height: '100%' }}
        tabIndex={0}
        typeof={type}
        virtualKeyboardMode="sandboxed"
      >
        {currentValue}
      </math-field>
    </div>
  )
}

const InAppKeyboard = forwardRef(InputKeyboard)
export default InAppKeyboard
